import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { AlertCircleIcon, ThermometerIcon, DropletIcon, BatteryChargingIcon, PlayIcon, PauseIcon } from 'lucide-react';
export function IntroSection() {
  const videoRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [videoPaused, setVideoPaused] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
    };
    checkMobile();
    // Throttled resize handler
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 250);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);
  // Lazy load video iframe when it's near the viewport
  useEffect(() => {
    if (!videoRef.current) return;
    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    };
    const loadVideo = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && videoRef.current && !videoLoaded) {
        // Create a custom video container with minimal controls
        const videoContainer = document.createElement('div');
        videoContainer.className = 'absolute inset-0';
        videoContainer.style.backgroundColor = '#000';
        // Create a thumbnail first for better performance
        const thumbnail = document.createElement('img');
        thumbnail.src = 'https://img.youtube.com/vi/b3zpzxkb1fs/maxresdefault.jpg';
        thumbnail.alt = 'Video thumbnail';
        thumbnail.className = 'absolute top-0 left-0 w-full h-full object-cover';
        thumbnail.loading = 'lazy';
        videoContainer.appendChild(thumbnail);
        // Create YouTube iframe with minimal parameters
        const iframe = document.createElement('iframe');
        iframe.className = 'absolute top-0 left-0 w-full h-full';
        // Set parameters to disable most controls but allow play/pause
        iframe.src = 'https://www.youtube.com/embed/b3zpzxkb1fs?autoplay=0&controls=0&disablekb=1&fs=0&modestbranding=1&rel=0&showinfo=0&loop=1&playlist=b3zpzxkb1fs&iv_load_policy=3&enablejsapi=1';
        iframe.title = 'Eye Treatment Video';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.frameBorder = '0';
        iframe.loading = 'lazy';
        iframe.id = 'intro-video-iframe';
        iframe.style.opacity = '0'; // Hide initially
        // Only replace thumbnail with iframe when clicked
        const playButton = document.createElement('div');
        playButton.className = 'absolute inset-0 flex items-center justify-center cursor-pointer';
        playButton.innerHTML = `
          <div class="bg-white bg-opacity-80 text-teal-600 rounded-full p-4 shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
          </div>
        `;
        videoContainer.appendChild(playButton);
        // Add click event to replace thumbnail with iframe
        playButton.addEventListener('click', () => {
          iframe.style.opacity = '1';
          playButton.style.display = 'none';
          thumbnail.style.display = 'none';
          videoContainer.appendChild(iframe);
          // Initialize YouTube API after click
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          const firstScriptTag = document.getElementsByTagName('script')[0];
          firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
          setVideoStarted(true);
          setVideoPaused(false);
        });
        if (videoRef.current) {
          videoRef.current.appendChild(videoContainer);
        }
        setVideoLoaded(true);
        observer.disconnect();
      }
    };
    const observer = new IntersectionObserver(loadVideo, options);
    observer.observe(videoRef.current);
    return () => {
      observer.disconnect();
    };
  }, [videoLoaded]);
  // Setup YouTube API
  useEffect(() => {
    // Define YouTube player
    let player: any = null;
    (window as any).onYouTubeIframeAPIReady = () => {
      if (!videoLoaded) return;
      const iframe = document.getElementById('intro-video-iframe');
      if (!iframe) return;
      player = new (window as any).YT.Player('intro-video-iframe', {
        events: {
          onReady: (event: any) => {
            // Player is ready
          },
          onStateChange: (event: any) => {
            // Update play/pause state
            setVideoPaused(event.data === (window as any).YT.PlayerState.PAUSED || event.data === (window as any).YT.PlayerState.ENDED);
            setVideoStarted(event.data !== (window as any).YT.PlayerState.ENDED);
          }
        }
      });
    };
    return () => {
      if (player) {
        player.destroy();
      }
      // Cleanup global callback
      delete (window as any).onYouTubeIframeAPIReady;
    };
  }, [videoLoaded]);
  // Custom play/pause controls
  const togglePlayPause = () => {
    const iframe = document.getElementById('intro-video-iframe') as HTMLIFrameElement;
    if (!iframe || !iframe.contentWindow) return;
    if (videoPaused) {
      // Play with sound
      iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      setVideoPaused(false);
      setVideoStarted(true);
    } else {
      // Pause
      iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      setVideoPaused(true);
    }
  };
  // Start video with sound
  const startVideoWithSound = () => {
    const iframe = document.getElementById('intro-video-iframe') as HTMLIFrameElement;
    if (!iframe || !iframe.contentWindow) return;
    // Start playing with sound
    iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    iframe.contentWindow.postMessage('{"event":"command","func":"unMute","args":""}', '*');
    setVideoStarted(true);
    setVideoPaused(false);
  };
  return <section className="py-12 border-b border-gray-200" data-id="element-449">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-450">
        What Standard Eye Care Often Overlooks…
      </h2>
      {/* Image section added below the heading */}
      <div className="mb-" data-id="element-451">
        <div className="max-w-3xl mx-auto" data-id="element-452">
          <div className="rounded-lg overflow-hidden shadow-lg" data-id="element-453">
            <img src="https://cdn.shopify.com/s/files/1/0679/7135/0570/files/imgi_1_imgi_32_gempages_581238827771757064-382547a2-553b-49a9-8bda-1929f2a7c35c_1.webp?v=1761116803" alt="Doctor in iRelief lab coat explaining eye anatomy with Oculis-3 device on desk" className="w-full h-auto object-cover" width="800" height="450" loading="lazy" data-id="element-454" />
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-8 items-center" data-id="element-455">
  <div className="md:w-full" data-id="element-456">
    <div className="bg-white rounded-lg p-6 shadow-md text-gray-800 leading-relaxed max-w-2xl mx-auto" data-id="element-457">
      <p className="text-gray-800 leading-relaxed mb-4" data-id="element-458">
  I've been there too. Sitting across from a doctor, hoping for answers, only to hear the same thing again and again:
  <br data-id="element-459" />
  <span className="font-semibold text-gray-900" data-id="element-460">"Just use more drops."</span>
  <br data-id="element-461" /><br data-id="element-462" />
  And honestly, I can’t even blame them.
          </p>
          <p className="mb-4" data-id="element-463">
        Most of us were trained from day one to recommend drops, because the same companies selling them are often the ones funding our clinics.
      </p>
      <p className="mb-4 font-medium text-gray-900" data-id="element-464">
       <strong data-id="element-465"> But here's the truth that you, me, and millions of others were never told…</strong>
      </p>
      <p className="mb-4" data-id="element-466">
        It was never just about <strong data-id="element-467">"dryness."</strong>
        <br data-id="element-468" />
        <span className="text-teal-600 font-semibold tm-4" data-id="element-469">And the data is devastating.</span>
      </p>
       <p className="mb-4" data-id="element-470">
          Patients using artificial tears 4+ times daily lost functioning oil glands{" "}
          <strong data-id="element-471">3.2× faster</strong> than those who used nothing at all.
          <br data-id="element-472" />
          <span className="font-semibold text-gray-900" data-id="element-473">Not slower. Faster.</span>
        </p>
      <p className="text-lg font-semibold text-gray-900 mb-4" data-id="element-474">
        Eye drops were designed for the wrong problem.
      </p>

      <p className="mb-4" data-id="element-475">
        When artificial tears were developed in the <strong data-id="element-476">1970s</strong>, researchers believed dry eyes were caused by{" "}
        <strong data-id="element-477">insufficient tear production.</strong>
      </p>

      <p className="mb-4 text-teal-600 font-semibold" data-id="element-478">They were wrong.</p>

      <p className="mb-4" data-id="element-479">
        We now know that <strong data-id="element-480">86% of dry eye cases</strong> aren't caused by lack of tears. They're caused by{" "}
        <strong data-id="element-481">clogged oil glands</strong> that can't seal in the tears that already exist.
      </p>

      <p className="font-semibold text-gray-900 mb-3" data-id="element-482">But here's where it gets sinister:</p>

      <p className="mb-4" data-id="element-483">
        The eye drop industry has known about <strong data-id="element-484">Meibomian Gland Dysfunction (MGD)</strong> since 2011. There are over{" "}
        <strong data-id="element-485">40 peer-reviewed studies</strong> confirming MGD as the primary cause. The{" "}
        <strong data-id="element-486">Tear Film & Ocular Surface Society</strong> issued updated treatment guidelines in 2017.
      </p>

      <p className="mb-4" data-id="element-487">
        Yet CVS shelves are still stocked with products that don't address the actual problem.
      </p>

      <p className="font-semibold text-gray-900" data-id="element-488">
        Because the solution isn't something you buy monthly for the rest of your life.
      </p>
    </div>
          <div className="mb-4" data-id="element-489">
            <img src="https://media2.giphy.com/media/v1.Y2lkPTc5MGI3NjExeXZjNXl4dmRkbjYxaTBibm1zOHR0OW5vdmkyZmwzZmV5ZzJudnRpayZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3ohjVbq7WI5mkxXyfu/giphy.gif" alt="Diagram showing healthy vs. clogged meibomian glands in the eyelid" className="rounded-lg w-full h-auto shadow-md" width="768" height="576" loading="lazy" data-id="element-490" />
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-8 mt-2" data-id="element-491">
        <div className="md:w-1/2" data-id="element-492">
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200" data-id="element-493">
            <h3 className="text-xl font-semibold text-gray-900" data-id="element-494">
              What's Throwing Your Eye's Natural Balance Off…?
            </h3>
            <ul className="space-y-4" data-id="element-495">
              <li className="flex" data-id="element-496">
                <div className="mt-1 mr-4" data-id="element-497">
                  <AlertCircleIcon className="w-6 h-6 text-red-500" data-id="element-498" />
                </div>
                <div data-id="element-499">
                  <p className="font-medium text-gray-900" data-id="element-500">
                    Digital Device Use
                  </p>
                  <p className="text-gray-700" data-id="element-501">
                    Hours on screens can cut blinking almost in half meaning the
                    natural protective oils don't always get released like they
                    should.
                  </p>
                </div>
              </li>
              <li className="flex" data-id="element-502">
                <div className="mt-1 mr-4" data-id="element-503">
                  <ThermometerIcon className="w-6 h-6 text-red-500" data-id="element-504" />
                </div>
                <div data-id="element-505">
                  <p className="font-medium text-gray-900" data-id="element-506">
                    Indoor Environment
                  </p>
                  <p className="text-gray-700" data-id="element-507">
                    Heating, AC, and low humidity speed up evaporation and leave
                    eyes feeling strained.
                  </p>
                </div>
              </li>
              <li className="flex" data-id="element-508">
                <div className="mt-1 mr-4" data-id="element-509">
                  <DropletIcon className="w-6 h-6 text-red-500" data-id="element-510" />
                </div>
                <div data-id="element-511">
                  <p className="font-medium text-gray-900" data-id="element-512">Aging</p>
                  <p className="text-gray-700" data-id="element-513">
                    Over time, the body simply produces less of the protective
                    oils that keep tears from vanishing too quickly.
                  </p>
                </div>
              </li>
              <li className="flex" data-id="element-514">
                <div className="mt-1 mr-4" data-id="element-515">
                  <BatteryChargingIcon className="w-6 h-6 text-red-500" data-id="element-516" />
                </div>
                <div data-id="element-517">
                  <p className="font-medium text-gray-900" data-id="element-518">Hormonal Changes</p>
                  <p className="text-gray-700" data-id="element-519">
                    Life shifts like starting birth control or going through
                    menopause can also change the way your body produces natural
                    oils."
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div className="md:w-1/2" data-id="element-520"></div>
      </div>
    </section>;
}